import {
    gql,
    useQuery
} from "@apollo/client";
import { useCallback } from "react";
import {
    FindAllUserPointExchangeHistoryQueryQuery,
    QueryFindAllUserPointExchangeHistoryArgs
} from "../../../../schemaTypes";

export const findAllUserPointExchangeHistoryQuery = gql`
  query FindAllUserPointExchangeHistoryQuery(
    $page: Int!
    $pageSize: Int!
    $sortOrder: String
  ) {
    findAllUserPointExchangeHistory(
      page: $page
      pageSize: $pageSize
      sortOrder: $sortOrder
    ) {
      data {
        id
        communityId
        communityName
        point
        rate
        exchangedPoint
        createdAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export type UserPointExchangeHistoryItem = NonNullable<
  NonNullable<
    NonNullable<FindAllUserPointExchangeHistoryQueryQuery["findAllUserPointExchangeHistory"]>["data"]
  >[number]
>;

export const useFindAllUserPointExchangeHistory = (
  variables: QueryFindAllUserPointExchangeHistoryArgs
) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<
    FindAllUserPointExchangeHistoryQueryQuery,
    QueryFindAllUserPointExchangeHistoryArgs
  >(findAllUserPointExchangeHistoryQuery, {
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const history = (data?.findAllUserPointExchangeHistory?.data || [])
    .filter((exchange): exchange is UserPointExchangeHistoryItem => Boolean(exchange));
  const page = data?.findAllUserPointExchangeHistory?.page || variables.page;
  const pageSize = data?.findAllUserPointExchangeHistory?.pageSize || variables.pageSize;
  const total = data?.findAllUserPointExchangeHistory?.total || 0;
  const totalPages = data?.findAllUserPointExchangeHistory?.totalPages || 0;
  const hasMoreHistory = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreHistory) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllUserPointExchangeHistory) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllUserPointExchangeHistory: {
            ...fetchMoreResult?.findAllUserPointExchangeHistory,
            data: [
              ...(prev?.findAllUserPointExchangeHistory?.data || []),
              ...(fetchMoreResult?.findAllUserPointExchangeHistory?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreHistory]);

  return {
    data: history,
    total,
    page,
    pageSize,
    totalPages,
    hasMoreHistory,
    loadMore,
    loading,
    error,
    refetch,
  };
};
